import { DialogActions, DialogContent, Divider, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useAppTFunction } from '@retail/app/i18n';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { AssortmentTagItemsUpdate } from '@retail/my-assortment/context';
import { AssortmentItemMinimal, AssortmentTag } from '@retail/my-assortment/types';
import { EditAssortmentTagsTable } from '@retail/products/components';
import { getActiveAssortmentTags } from '@retail/products/utils';
import { getISO_8601Date } from '@retail/utils';
import { ContainedButton, OutlinedButton, TextButton } from '@shared/components';
import { useCallback } from 'react';

interface Props {
  assortmentItems: AssortmentItemMinimal[];
  assortmentTags: AssortmentTag[];
  isSaving: boolean;
  continueToCustomerAssortments: boolean;
  onAddItems: (items: AssortmentItemMinimal[], tagUpdates: AssortmentTagItemsUpdate[]) => void;
  onClose: () => void;
  goBack: () => void;
}

export function HandleItemTags({
  assortmentItems,
  assortmentTags,
  isSaving,
  continueToCustomerAssortments,
  onAddItems,
  onClose,
  goBack,
}: Props) {
  const t = useAppTFunction();
  const apiRef = useGridApiRef();
  const { isBrandContext } = useSelectedOrgUnit();

  const onSubmit = useCallback(() => {
    const taggedItems = apiRef.current
      ? (Array.from(apiRef.current.getRowModels().values()) as AssortmentItemMinimal[])
      : [];

    const tagItemsByTagId = new Map<number, AssortmentTagItemsUpdate['items']>();
    taggedItems.forEach((item) =>
      getActiveAssortmentTags(item.assortmentTags).forEach(({ tagId, validFrom, validTo }) =>
        tagItemsByTagId.set(tagId, [
          ...(tagItemsByTagId.get(tagId) ?? []),
          {
            supplierItemId: item.mgSupplierItemId,
            validFrom: getISO_8601Date(isBrandContext ? validFrom : new Date()),
            validTo: isBrandContext && validTo ? getISO_8601Date(validTo) : undefined,
          },
        ])
      )
    );

    onAddItems(
      taggedItems,
      Array.from(tagItemsByTagId, ([tagId, items]) => ({ tagId, items }))
    );
  }, [apiRef, isBrandContext, onAddItems]);

  return (
    <>
      <DialogContent
        sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}
        className="padding-0"
      >
        <EditAssortmentTagsTable
          apiRef={apiRef}
          assortmentItems={assortmentItems}
          assortmentTags={assortmentTags}
          hasDatedAssortmentTags={isBrandContext}
        />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextButton onClick={onClose}>{t('products.cancel')}</TextButton>

          <OutlinedButton color="secondary" size="small" onClick={goBack}>
            {t('common.goBack')}
          </OutlinedButton>

          <ContainedButton color="secondary" size="small" loading={isSaving} onClick={onSubmit}>
            {continueToCustomerAssortments
              ? t('myAssortment.actions.continueToCustomerAssortments')
              : t('myAssortment.actions.addItems')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </>
  );
}
